import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Cookie, Settings, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface ConsentState {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CONSENT_VERSION = '1.0';

export const CookieConsentBanner: React.FC = () => {
  const { user } = useAuth();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState<ConsentState>({
    essential: true, // Always required
    analytics: false,
    marketing: false,
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    checkConsentStatus();
  }, [user]);

  const checkConsentStatus = async () => {
    // First check localStorage for quick UI response (works for both anonymous and logged-in users)
    const localConsent = localStorage.getItem('cookie_consent');
    
    if (localConsent) {
      try {
        const parsed = JSON.parse(localConsent);
        if (parsed.version === CONSENT_VERSION) {
          setConsent(parsed.consent);
          setShowBanner(false);
          return; // Already consented with current version
        }
      } catch {
        // Invalid localStorage data, continue to show banner
      }
    }

    // If user is logged in, also check database for synced consent
    if (user) {
      const { data } = await supabase
        .from('user_consents')
        .select('*')
        .eq('user_id', user.id)
        .eq('consent_type', 'cookies')
        .single();

      if (data && data.consented && data.version_consented_to === CONSENT_VERSION) {
        // Sync to localStorage for faster future checks
        localStorage.setItem('cookie_consent', JSON.stringify({
          version: CONSENT_VERSION,
          consent: { essential: true, analytics: true, marketing: true },
          timestamp: data.consented_at
        }));
        setShowBanner(false);
        return;
      }
    }

    // Show banner if no valid consent found (for both anonymous and logged-in users)
    setShowBanner(true);
  };

  const saveConsent = async (consentState: ConsentState) => {
    setIsSaving(true);
    
    try {
      // Save to localStorage for immediate effect
      localStorage.setItem('cookie_consent', JSON.stringify({
        version: CONSENT_VERSION,
        consent: consentState,
        timestamp: new Date().toISOString()
      }));

      // If user is logged in, save to database
      if (user) {
        // Upsert cookie consent
        await supabase
          .from('user_consents')
          .upsert({
            user_id: user.id,
            consent_type: 'cookies',
            consented: true,
            consented_at: new Date().toISOString(),
            version_consented_to: CONSENT_VERSION,
            user_agent: navigator.userAgent,
          }, {
            onConflict: 'user_id,consent_type'
          });

        // Also record specific consent types
        const consentTypes = [
          { type: 'cookies_essential', value: consentState.essential },
          { type: 'cookies_analytics', value: consentState.analytics },
          { type: 'cookies_marketing', value: consentState.marketing },
        ];

        for (const ct of consentTypes) {
          await supabase
            .from('user_consents')
            .upsert({
              user_id: user.id,
              consent_type: ct.type,
              consented: ct.value,
              consented_at: ct.value ? new Date().toISOString() : null,
              withdrawn_at: !ct.value ? new Date().toISOString() : null,
              version_consented_to: CONSENT_VERSION,
            }, {
              onConflict: 'user_id,consent_type'
            });
        }
      }

      setShowBanner(false);
      setShowSettings(false);
    } catch (error) {
      console.error('Error saving consent:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAcceptAll = () => {
    const allConsent = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    setConsent(allConsent);
    saveConsent(allConsent);
  };

  const handleAcceptEssential = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false,
    };
    setConsent(essentialOnly);
    saveConsent(essentialOnly);
  };

  const handleSavePreferences = () => {
    saveConsent(consent);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background/80 backdrop-blur-sm border-t">
      <div className="max-w-4xl mx-auto">
        {!showSettings ? (
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="flex items-center gap-3 flex-shrink-0">
                  <Cookie className="h-8 w-8 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">Cookie Preferences</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    We use cookies to enhance your experience. Essential cookies are required for the site to function. 
                    You can customize your preferences or accept all cookies.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowSettings(true)}
                    className="flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Customize
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleAcceptEssential}
                    disabled={isSaving}
                  >
                    Essential Only
                  </Button>
                  <Button 
                    onClick={handleAcceptAll}
                    disabled={isSaving}
                  >
                    Accept All
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Cookie className="h-5 w-5" />
                    Cookie Settings
                  </CardTitle>
                  <CardDescription>
                    Manage your cookie preferences below
                  </CardDescription>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setShowSettings(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between py-2 border-b">
                <div>
                  <Label className="font-medium">Essential Cookies</Label>
                  <p className="text-sm text-muted-foreground">
                    Required for the website to function. Cannot be disabled.
                  </p>
                </div>
                <Switch checked={true} disabled />
              </div>

              <div className="flex items-center justify-between py-2 border-b">
                <div>
                  <Label className="font-medium">Analytics Cookies</Label>
                  <p className="text-sm text-muted-foreground">
                    Help us understand how you use our site to improve it.
                  </p>
                </div>
                <Switch 
                  checked={consent.analytics}
                  onCheckedChange={(checked) => 
                    setConsent(prev => ({ ...prev, analytics: checked }))
                  }
                />
              </div>

              <div className="flex items-center justify-between py-2 border-b">
                <div>
                  <Label className="font-medium">Marketing Cookies</Label>
                  <p className="text-sm text-muted-foreground">
                    Used to show you relevant advertisements.
                  </p>
                </div>
                <Switch 
                  checked={consent.marketing}
                  onCheckedChange={(checked) => 
                    setConsent(prev => ({ ...prev, marketing: checked }))
                  }
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={handleAcceptEssential}
                  disabled={isSaving}
                >
                  Essential Only
                </Button>
                <Button 
                  onClick={handleSavePreferences}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Preferences'}
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center pt-2">
                For more information, see our{' '}
                <a href="/privacy-policy" className="underline hover:text-foreground">
                  Privacy Policy
                </a>
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
